<template>
  <header :class="['header-root fixed top-0 left-0 right-0 z-50 flex items-start justify-center', headerSearchPage && 'header-root--search-page']">
    <!-- Morphing pill -->
    <div ref="pillRef" :class="['header-pill', headerSearchPage && 'header-pill--search-page']">
      <!-- Logo slot — always left, never animated -->
      <NuxtLink
        :to="$localePath('/')"
        class="slot-logo"
      >
        <NuxtImg src="/logo.svg" alt="Bubba" width="108" height="28" class="h-7 w-auto" />
      </NuxtLink>

      <!-- Services button wrapper (animated — GSAP controls width & opacity; hidden on search page) -->
      <div v-if="!headerSearchPage" ref="browseRightWrapRef" class="anim-wrap anim-wrap--browse-right">
        <div ref="servicesRef" class="relative flex items-stretch">
          <button
            type="button"
            class="anim-inner anim-inner--browse"
            :tabindex="heroSearchVisible ? -1 : 0"
            @click="toggleServicesPanel"
          >
            <Icon name="tabler:layout-grid" size="16" />
            <span>{{ $ts('nav.services') }}</span>
            <Icon
              name="tabler:chevron-down"
              size="12"
              :class="['shrink-0 opacity-50 transition-transform duration-200', isServicesPanelOpen && 'rotate-180']"
            />
          </button>

          <!-- Services dropdown panel — teleported to body so it clears overflow:hidden pill -->
          <Teleport to="body">
            <Transition
              enter-active-class="transition ease-out duration-150"
              enter-from-class="opacity-0 -translate-y-1"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition ease-in duration-100"
              leave-from-class="opacity-100 translate-y-0"
              leave-to-class="opacity-0 -translate-y-1"
            >
              <div
                v-if="isServicesPanelOpen"
                :style="servicesPanelStyle"
                class="fixed z-[200] overflow-hidden rounded-2xl border border-border bg-white shadow-2xl"
                @mousedown.stop
              >
                <div class="px-3 py-3">
                  <p class="mb-2 px-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Choose a service
                  </p>
                  <div class="grid grid-cols-2 gap-0.5 sm:grid-cols-3">
                    <!-- Any service option — only in header panel -->
                    <button
                      type="button"
                      class="service-card flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-muted/60 active:scale-[0.97]"
                      @mousedown.prevent="selectService(null)"
                    >
                      <Icon name="tabler:layout-grid" size="22" class="shrink-0 text-muted-foreground" />
                      <span class="truncate text-sm font-semibold">Any service</span>
                    </button>
                    <button
                      v-for="service in SERVICE_TYPES"
                      :key="service.key"
                      type="button"
                      class="service-card flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-muted/60 active:scale-[0.97]"
                      @mousedown.prevent="selectService(service)"
                    >
                      <Icon :name="service.icon" size="22" class="shrink-0" />
                      <span class="truncate text-sm font-semibold">{{ service.label }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </Transition>
          </Teleport>
        </div>
      </div>

      <!-- Sign up / profile slot — always visible, pinned right -->
      <div class="slot-signup-wrap">
        <!-- Mobile only: hamburger -->
        <button
          type="button"
          class="slot-hamburger rounded-r-full"
          :aria-label="$ts('nav.openMenu')"
          @click="isMobileMenuOpen = true"
        >
          <Icon name="tabler:menu-2" size="20" />
        </button>

        <!-- Desktop: logged in -->
        <template v-if="session">
          <NuxtLink
            :to="$localePath('/account')"
            class="slot-signup slot-auth rounded-r-full"
          >
            <img
              v-if="session.user?.image"
              :src="session.user.image"
              :alt="session.user.name ?? ''"
              class="h-7 w-7 rounded-full object-cover"
            />
            <span
              v-else
              class="flex h-7 w-7 items-center justify-center rounded-full bg-muted"
            >
              <Icon name="tabler:user" size="16" class="text-muted-foreground" />
            </span>
            <span class="sr-only">{{ $ts('nav.profile') }}</span>
          </NuxtLink>
        </template>

        <!-- Desktop: not logged in -->
        <template v-else>
          <button
            type="button"
            class="slot-signup slot-auth rounded-r-full"
            @click="authPanelTab = 'register'; isAuthPanelOpen = true"
          >
            <Icon name="tabler:arrow-right" size="15" />
            {{ $ts('nav.signUp') }}
          </button>
        </template>
      </div>
    </div>
  </header>

  <!-- Mobile slide-in menu -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="isMobileMenuOpen" class="fixed inset-0 z-[60] flex">
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-foreground/30 backdrop-blur-sm"
          aria-hidden="true"
          @click="isMobileMenuOpen = false"
        />
        <!-- Panel -->
        <div class="absolute inset-y-0 right-0 flex w-[280px] flex-col bg-card shadow-2xl">
          <div class="flex items-center justify-between border-b border-border px-4 py-3">
            <NuxtLink :to="$localePath('/')" @click="isMobileMenuOpen = false">
              <NuxtImg src="/logo.svg" alt="Bubba" width="88" height="22" class="h-5.5 w-auto" />
            </NuxtLink>
            <button
              type="button"
              class="flex h-9 w-9 items-center justify-center rounded-full bg-muted transition-colors hover:bg-muted/80"
              :aria-label="$ts('nav.closeMenu')"
              @click="isMobileMenuOpen = false"
            >
              <Icon name="tabler:x" size="18" />
            </button>
          </div>

          <div class="flex-1 overflow-y-auto p-4">
            <NuxtLink
              :to="$localeRoute('/services')"
              class="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
              @click="isMobileMenuOpen = false"
            >
              <Icon name="tabler:layout-grid" size="18" />
              {{ $t('nav.viewAllServices') }}
            </NuxtLink>

            <div class="my-3 h-px bg-border" />

            <NuxtLink
              :to="$localePath('/partners')"
              class="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              @click="isMobileMenuOpen = false"
            >
              <Icon name="tabler:building-store" size="18" />
              {{ $t('nav.forBusiness') }}
            </NuxtLink>
          </div>

          <div class="space-y-2 border-t border-border p-4">
            <template v-if="!session">
              <button
                type="button"
                class="w-full rounded-full border border-border px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                @click="authPanelTab = 'login'; isAuthPanelOpen = true; isMobileMenuOpen = false"
              >
                {{ $ts('nav.login') }}
              </button>
              <button
                type="button"
                class="w-full rounded-full bg-foreground px-4 py-2.5 text-sm font-medium text-background transition-colors hover:bg-foreground/90"
                @click="authPanelTab = 'register'; isAuthPanelOpen = true; isMobileMenuOpen = false"
              >
                {{ $ts('nav.signUp') }}
              </button>
            </template>
            <template v-else>
              <NuxtLink
                :to="$localePath('/account')"
                class="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                @click="isMobileMenuOpen = false"
              >
                <img
                  v-if="session?.user?.image"
                  :src="session.user.image"
                  :alt="session.user.name ?? ''"
                  class="h-6 w-6 rounded-full object-cover"
                />
                <span
                  v-else
                  class="flex h-6 w-6 items-center justify-center rounded-full bg-muted"
                >
                  <Icon name="tabler:user" size="14" class="text-muted-foreground" />
                </span>
                {{ $t('nav.myAccount') }}
              </NuxtLink>
            </template>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- Auth panel -->
  <BaseAuthPanel
    :is-open="isAuthPanelOpen"
    :initial-tab="authPanelTab"
    @close="isAuthPanelOpen = false"
    @authenticated="handleAuthenticated"
  />

  <!-- Scroll to top -->
  <Teleport to="body">
    <Transition name="scroll-top">
      <button
        v-if="scrolledDown"
        class="scroll-top-btn fixed right-6 z-50 flex h-16 w-16 items-center justify-center rounded-full transition-transform hover:scale-110 active:scale-95"
        :class="scrollTopClass"
        :aria-label="$ts('nav.scrollToTop')"
        @click="scrollToTop"
      >
        <Icon name="tabler:arrow-up" size="26" class="text-foreground" />
      </button>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount, nextTick, computed } from 'vue';
import { navigateTo, useI18n, useState, useRoute } from '#imports';
import { gsap } from 'gsap';
import { useAuthClient } from '#composables/useAuthClient';
import { getCachedSession } from '#bubba/shared/utils/sessionCache';
import { usePostHog } from '#composables/usePostHog';
import { SERVICE_TYPES } from '#components/ServicePicker.vue';
import type { ServiceType } from '#components/ServicePicker.vue';

const authClient = useAuthClient();
const { $t, $ts, $localePath, $localeRoute } = useI18n();
const { captureEvent } = usePostHog();
const route = useRoute();

const heroSearchVisible = useState<boolean>('heroSearchVisible', () => false);
const headerSearchPage = useState<boolean>('headerSearchPage', () => false);

const isAuthPanelOpen = ref(false);
const authPanelTab = ref<'login' | 'register'>('login');
const isMobileMenuOpen = ref(false);
const scrolledDown = ref(false);
const session = ref<{ user?: { id: string; email?: string; name?: string; image?: string | null } | null } | null>(null);

// Position the scroll-to-top FAB above the support chat FAB when it's visible,
// or at the standard bottom edge when the support FAB is suppressed on the current page.
const scrollTopClass = computed(() =>
  route.meta.hideSupportFab ? 'bottom-6' : 'bottom-24',
);

// GSAP element refs
const pillRef           = ref<HTMLElement | null>(null);
const browseRightWrapRef = ref<HTMLElement | null>(null);

// Services panel
const servicesRef          = ref<HTMLElement | null>(null);
const isServicesPanelOpen  = ref(false);
const servicesPanelStyle   = ref<Record<string, string>>({});

function updateServicesPanelPosition() {
  const rect = servicesRef.value?.getBoundingClientRect();
  if (!rect) return;
  servicesPanelStyle.value = {
    top:  `${rect.bottom + 8}px`,
    left: `${rect.left}px`,
  };
}

function toggleServicesPanel() {
  if (isServicesPanelOpen.value) {
    isServicesPanelOpen.value = false;
  } else {
    updateServicesPanelPosition();
    isServicesPanelOpen.value = true;
  }
}

function selectService(service: ServiceType | null) {
  isServicesPanelOpen.value = false;
  captureEvent('header_service_selected', { service: service?.key ?? 'any' });
  if (service) {
    navigateTo($localeRoute(`/services?type=${service.key}`));
  } else {
    navigateTo($localeRoute('/services'));
  }
}

function onDocumentClick(e: MouseEvent) {
  if (!isServicesPanelOpen.value) return;
  if (servicesRef.value?.contains(e.target as Node)) return;
  isServicesPanelOpen.value = false;
}

let morphTl: gsap.core.Timeline | null = null;
let ctx: gsap.Context | null = null;

function onScroll() {
  scrolledDown.value = window.scrollY > 300;
}

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

onMounted(async () => {
  try {
    const result = await getCachedSession(authClient);
    session.value = result?.data ?? null;
  } catch {
    session.value = null;
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  document.addEventListener('click', onDocumentClick, true);

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobileViewport = window.innerWidth < 768;

  // Search page: pill is full-width via CSS — skip GSAP animation entirely
  if (headerSearchPage.value) return;

  ctx = gsap.context(() => {
    if (!pillRef.value || !browseRightWrapRef.value) return;

    gsap.set(browseRightWrapRef.value, { width: 0, opacity: 0 });
    if (!isMobileViewport) {
      gsap.set(pillRef.value, { width: Math.min(580, window.innerWidth - 32) });
    }

    if (!reduced) {
      morphTl = gsap.timeline({ paused: true });
      if (!isMobileViewport) {
        morphTl.to(
          pillRef.value,
          { width: Math.min(680, window.innerWidth - 32), duration: 0.48, ease: 'power2.inOut' },
          0,
        );
      }
      morphTl.to(
        browseRightWrapRef.value,
        { width: 140, opacity: 1, duration: 0.38, ease: 'power2.inOut' },
        0,
      );
    }

    if (!heroSearchVisible.value) {
      if (reduced) {
        if (!isMobileViewport) gsap.set(pillRef.value, { width: Math.min(680, window.innerWidth - 32) });
        gsap.set(browseRightWrapRef.value, { width: 140, opacity: 1 });
      } else {
        morphTl?.progress(1);
      }
    }
  });

  watch(heroSearchVisible, (visible) => {
    const isReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mobile = window.innerWidth < 768;

    if (isReduced) {
      if (visible) {
        if (!mobile && pillRef.value) gsap.set(pillRef.value, { width: Math.min(580, window.innerWidth - 32) });
        if (browseRightWrapRef.value) gsap.set(browseRightWrapRef.value, { width: 0, opacity: 0 });
      } else {
        if (!mobile && pillRef.value) gsap.set(pillRef.value, { width: Math.min(680, window.innerWidth - 32) });
        if (browseRightWrapRef.value) gsap.set(browseRightWrapRef.value, { width: 140, opacity: 1 });
      }
      return;
    }

    if (visible) {
      morphTl?.reverse();
    } else {
      morphTl?.play();
    }
  });
});

async function handleAuthenticated() {
  try {
    const result = await getCachedSession(authClient);
    session.value = result?.data ?? null;
  } catch {
    session.value = null;
  }
}

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll);
  document.removeEventListener('click', onDocumentClick, true);
  ctx?.revert();
});
</script>

<style scoped>
/* ── Root — pointer-events: none lets clicks fall through transparent area around pill ── */
.header-root {
  pointer-events: none;
}

.header-root > * {
  pointer-events: auto;
}

/* Mobile: header becomes pointer-events auto (pill spans full width) */
@media (max-width: 767px) {
  .header-root {
    pointer-events: auto;
  }
}

/* ── Morphing pill ── */
.header-pill {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  overflow: hidden;
  height: 4rem;
  margin-top: 1rem;
  border-radius: 9999px;
  border: 1px solid var(--shadow-color-md);
  background: var(--card);
  box-shadow: 0 4px 24px var(--shadow-color-xl);
}

/* Mobile: full-width flat bar */
@media (max-width: 767px) {
  .header-pill {
    width: 100% !important;
    margin-top: 0;
    border-radius: 0;
    border-left: none;
    border-right: none;
    border-top: none;
    box-shadow: none;
  }
}

/* ── Search-page state: full-width flat bar (no pill, no margin, no shadow) ── */
.header-root--search-page {
  pointer-events: auto;
}

.header-pill--search-page {
  width: 100% !important;
  margin-top: 0;
  border-radius: 0;
  border-left: none;
  border-right: none;
  border-top: none;
  box-shadow: none;
}

/* ── Animated wrappers (GSAP controls width and opacity) ── */
.anim-wrap {
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
  overflow: hidden;
}

/* Hide animated slots on mobile */
@media (max-width: 767px) {
  .anim-wrap--browse-right {
    display: none;
  }
}

/* ── Inner elements ── */
.anim-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  align-self: stretch;
  white-space: nowrap;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--foreground);
  cursor: pointer;
  background: none;
  border: none;
  text-decoration: none;
  transition: background 200ms;
}

.anim-inner:hover {
  background: color-mix(in srgb, var(--muted) 50%, transparent);
}

.anim-inner--browse {
  width: 140px;
  padding-left: 1.25rem;
  padding-right: 1rem;
  border-left: 1px solid var(--shadow-color-md);
}

/* ── Logo slot ── */
.slot-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  flex-shrink: 0;
  padding-left: 1.25rem;
  padding-right: 0.75rem;
  text-decoration: none;
  transition: background 200ms;
}

.slot-logo:hover {
  background: color-mix(in srgb, var(--muted) 50%, transparent);
}

/* ── Sign up / profile slot ── */
.slot-signup-wrap {
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
  margin-left: auto;
  border-left: 1px solid var(--shadow-color-md);
}

@media (max-width: 767px) {
  .slot-signup-wrap {
    border-left: none;
  }
}

.slot-signup {
  align-self: stretch;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--foreground);
  white-space: nowrap;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: none;
  transition: background 200ms;
}

.slot-signup:hover {
  background: color-mix(in srgb, var(--muted) 50%, transparent);
}

.slot-auth {
  display: none;
}

@media (min-width: 768px) {
  .slot-auth {
    display: flex;
  }
}

.slot-hamburger {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  padding-left: 1rem;
  padding-right: 1rem;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--foreground);
  transition: background 200ms;
}

.slot-hamburger:hover {
  background: color-mix(in srgb, var(--muted) 50%, transparent);
}

@media (min-width: 768px) {
  .slot-hamburger {
    display: none;
  }
}

/* ── Scroll-to-top button ── */
.scroll-top-btn {
  background: oklch(1 0 90 / 0.9);
  border: 1.5px solid var(--shadow-color-2xl);
  box-shadow: 0 2px 12px var(--shadow-color-lg);
}

.scroll-top-enter-active,
.scroll-top-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.scroll-top-enter-from,
.scroll-top-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
